import React, { createContext, type ReactNode, Suspense, useContext, useMemo } from 'react';

import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import { GlobalSideNavigationAGGRelayConnection } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnection';
import type {
	ShortcutsConfig,
	ShortcutsSlotProps,
} from '@atlassian/global-side-navigation/common/types';
import type { GlobalSideNavigationAGGRelayConnectionQuery$data } from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnectionQuery.graphql';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { useNetworkStorageParams } from './hooks/useNetworkStorageParams.ts';
import { useGlobalAppShortcuts } from './hooks/useGlobalAppShortcuts.ts';
import { useForYouConfig } from './hooks/useForYouConfig.tsx';
import { useMenuItemsConfig } from './hooks/useMenuItemsConfig.ts';
import { SafeInvitedAppShortcutsRecommendations } from './AppShortcuts/InvitedAppShortcutsRecommendations.tsx';
import { CustomHomeItem } from './components/CustomHomeItem.tsx';
import { useShortcutVisibilityOverrides } from './AppShortcuts/hooks/useShortcutVisibilityOverrides.tsx';
import { useDefaultMenuVisibility } from './hooks/useDefaultMenuVisibility.ts';
import type { MenuVisibility } from './hooks/useDefaultMenuVisibility.ts';

// GlobalSideNavigation persists its resized width to network storage only. The Suspense fallback
// skeleton (SideNavigationSkeleton in @confluence/page-layout) sizes itself from the legacy
// localStorage key PERSISTED_LEFT_SIDEBAR_WIDTH, so without this mirror the skeleton renders at the
// stale/default width and visibly snaps to the real width once the nav loads. Mirroring the
// committed width keeps the skeleton and the loaded nav in sync.
const mirrorSideNavWidthToLocalStorage = ({ finalWidth }: { finalWidth: number }) => {
	confluenceLocalStorageInstance.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_WIDTH,
		finalWidth,
	);
};

// Stable empty config used for the `hideGSN` variants of GSN's `forYou` / `items` props, so those
// props do not change identity on every render (which would defeat `React.memo(GlobalSideNavigation)`).
const EMPTY_CONFIG = {};

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

// Carries the recommendation inputs (cloudId, userId) to the ShortcutsSlot component below, which GSN
// renders deep inside its own tree. Context (rather than a closure) lets the slot live at module
// scope with a stable identity, so GSN never remounts the shortcuts subtree. The GRS recommendations
// query itself is read INSIDE GSN by `SafeInvitedAppShortcutsRecommendations` (via `useLazyLoadQuery`
// against the ambient `platformEnv`), so the SSR-preloaded record is served synchronously — only the
// cloudId and userId travel through context.
const InvitedAppShortcutsSlotContext = createContext<{
	cloudId: string;
	userId: string;
} | null>(null);

/**
 * `ShortcutsSlot` implementation for the invited-app shortcuts.
 *
 * Eligibility comes from the SSR-preloaded GRS recommendations query, read with `useLazyLoadQuery`
 * inside `SafeInvitedAppShortcutsRecommendations`, which scopes Suspense (cold Relay store) and a
 * fail-open ErrorBoundary to the shortcuts subtree — so the rest of the nav is never blocked.
 */
function InvitedAppShortcutsSlot({
	Component,
	shortcutItems,
}: ShortcutsSlotProps): React.JSX.Element {
	const slotContext = useContext(InvitedAppShortcutsSlotContext);

	const shortcutsConfig = useMemo(
		() =>
			shortcutItems.reduce<ShortcutsConfig>((acc, item, index) => {
				acc[String(index)] = item;
				return acc;
			}, {}),
		[shortcutItems],
	);

	// Defensive: the provider always supplies these inputs on this path; if they are ever
	// missing, fail open to the default (undecorated) menu.
	if (!slotContext) {
		return <Component />;
	}

	return (
		<SafeInvitedAppShortcutsRecommendations
			Component={Component}
			shortcutsConfig={shortcutsConfig}
			cloudId={slotContext.cloudId}
			userId={slotContext.userId}
		/>
	);
}

type PlatformNavigationComponentProps = {
	content?: ReactNode; //react compoenent to show for any given view
	hideGSN?: boolean; //boolean to tell us if the current view should hide the global & GAS section
	scrollTreeToCurrent?: () => void; //function that should be fired whenever the navigation is expanded to scroll to the current pageTree page in the tree once implemented remove optional type
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>; //optional ref passed in to be attached to SideNavigation platform component
	bottomElem?: ReactNode; // optional element to be rendered at the bottom of the side navigation
};

export const PlatformNavigationComponent = ({
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	hideGSN,
	bottomElem,
}: PlatformNavigationComponentProps): React.JSX.Element => {
	const { accessStatus, cloudId, userId } = useSessionData();
	const networkStorageParams = useNetworkStorageParams();
	const { shortcuts: globalAppShortcuts, createTeamModalComponent } = useGlobalAppShortcuts();
	const forYouConfig = useForYouConfig();
	const itemsConfig = useMenuItemsConfig();
	const { homepageUri } = useContext(SPAViewContext);
	const { isCompanyHubVisible } = useShortcutVisibilityOverrides();
	const defaultMenuVisibility = useDefaultMenuVisibility();

	const isCompanyHubSetAsCustomHome =
		isCompanyHubVisible && homepageUri.includes(getCompanyHubSpaceKey());
	const isCustomHomeVisible: boolean =
		!isCompanyHubSetAsCustomHome && !['/spa.action', '', 'homepageUri'].includes(homepageUri);

	// `userId` is the same signal preloadPlatformNavigationQuery guards on, and it is session-stable,
	// so `queryRef` is either present for the whole session or absent for the whole session — which
	// avoids the subtree remount that a queryRef flipping between defined and undefined would cause.
	const isAnonymous = !userId;

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	// Eligibility comes from the SSR-preloaded GRS recommendations query, rendered through the slot so
	// Suspense/ErrorBoundary stay scoped to the shortcuts subtree. Requires cloudId + userId (the
	// preloader only fired when both were present).
	const showInvitedAppShortcuts = showGlobalAppShortcuts && !hideGSN && !!cloudId && !!userId;

	// Stable identity for the slot context value (perf-linting/no-inline-context-value). Null unless
	// both ids are present, so consumers never receive empty-string sentinels — the slot fails open
	// when the value is null.
	const slotContextValue = useMemo(
		() => (cloudId && userId ? { cloudId, userId } : null),
		[cloudId, userId],
	);

	const gsnProps = {
		cloudId,
		isAnonymous,
		forYouConfig,
		isCustomHomeVisible,
		itemsConfig,
		content,
		onExpand,
		onCollapse,
		onPeekStart,
		onPeekEnd,
		sideNavContentRef,
		globalAppShortcuts,
		showInvitedAppShortcuts,
		networkStorageParams,
		defaultMenuVisibility,
		hideGSN,
		bottomElem,
	};

	return (
		<InvitedAppShortcutsSlotContext.Provider value={slotContextValue}>
			{isAnonymous ? (
				/*
				 * Anonymous users skip GlobalSideNavigationAGGRelayConnection entirely: without a
				 * userId there is no ownerAri, and both fields the query would select are keyed on
				 * it, so AGG rejects the request (see preloadPlatformNavigationQuery, which mirrors
				 * this check server-side). Rendering GSN without a `queryRef` takes its non-Relay
				 * path, and `isAnonymous` is what makes that safe — it swaps NetworkStore (whose
				 * invariant requires a queryRef) for LocalStore, and disables menu customization
				 * and shortcut fetching, none of which anonymous users can persist anyway.
				 *
				 * No Suspense boundary here because nothing on this branch can suspend.
				 */
				<ConfluenceGSN {...gsnProps} />
			) : (
				/*
				 * Suspense catches useLazyLoadQuery suspensions from GlobalSideNavigationAGGRelayConnection
				 * on pages where no SSR AGG cache was injected (e.g. space settings pages, which are proxied
				 * to the monolith and don't go through Bifrost SSR). Without this boundary, the suspension
				 * would propagate upward — SuspenseWithDataFetcher only adds a Suspense wrapper when
				 * window.__SSR_REACT_STREAMING__ is true, so non-SSR pages have no boundary to catch it,
				 * causing React error 426 and a hard crash.
				 *
				 * Using null fallback is intentional: the GSN will load once the network request resolves.
				 * On SSR pages the cache is always populated so suspension never occurs in practice.
				 */
				<Suspense fallback={null}>
					<GlobalSideNavigationAGGRelayConnection {...networkStorageParams}>
						{({ queryRef }) => <ConfluenceGSN queryRef={queryRef} {...gsnProps} />}
					</GlobalSideNavigationAGGRelayConnection>
				</Suspense>
			)}
			{createTeamModalComponent}
		</InvitedAppShortcutsSlotContext.Provider>
	);
};

type ConfluenceGSNProps = {
	// Absent for anonymous users, who skip GlobalSideNavigationAGGRelayConnection altogether.
	queryRef?: GlobalSideNavigationAGGRelayConnectionQuery$data;
	isAnonymous: boolean;
	cloudId: string | undefined;
	forYouConfig: ReturnType<typeof useForYouConfig>;
	isCustomHomeVisible: boolean;
	itemsConfig: ReturnType<typeof useMenuItemsConfig>;
	content?: ReactNode;
	onExpand?: () => void;
	onCollapse?: () => void;
	onPeekStart?: () => void;
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;
	hideGSN?: boolean;
	sideNavContentRef?: React.Ref<HTMLDivElement | null>;
	globalAppShortcuts: ReturnType<typeof useGlobalAppShortcuts>['shortcuts'];
	showInvitedAppShortcuts: boolean;
	networkStorageParams: ReturnType<typeof useNetworkStorageParams>;
	defaultMenuVisibility: MenuVisibility[];
	bottomElem?: ReactNode;
};

/**
 * Renders the GlobalSideNavigation with the appropriate props.
 * Extracted as a component (rather than a render function) so React can properly
 * manage its lifecycle and avoid unnecessary re-renders.
 */
function ConfluenceGSN({
	queryRef,
	isAnonymous,
	cloudId,
	forYouConfig,
	isCustomHomeVisible,
	itemsConfig,
	content,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	sideNavContentRef,
	globalAppShortcuts, //todo: when hideGSN is true we dont' need to query for these
	showInvitedAppShortcuts,
	networkStorageParams,
	defaultMenuVisibility,
	hideGSN,
	bottomElem,
}: ConfluenceGSNProps): React.JSX.Element | null {
	// NetworkStorageParams includes queryRef typed as GlobalSideNavigationAGGRelayConnectionQuery$data.
	// The cast is needed because CI typechecks against pre-built declarations which may not have
	// resolved the Relay-generated type. Runtime behaviour is correct.
	//
	// Memoised because `React.memo(GlobalSideNavigation)` compares this prop by reference, and
	// `OptionsProvider` publishes it as part of its context value. Building it inline meant GSN
	// re-rendered its entire tree on every render of this component (COPPER-1474).
	//
	// `queryRef` is omitted rather than set to undefined when absent (anonymous users): GSN selects
	// its non-Relay path on a truthiness check, and `isAnonymous` routes the customization store to
	// LocalStore, which — unlike NetworkStore — has no invariant requiring a queryRef.
	const networkStorageParamsWithQueryRef = useMemo(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		() => (queryRef ? ({ queryRef, ...networkStorageParams } as any) : networkStorageParams),
		[queryRef, networkStorageParams],
	);
	// Memoised for the same reason as `networkStorageParamsWithQueryRef` above: `SideNav` memoises
	// `preMainElem` by reference, so a fresh element here cascades a re-render down the whole nav.
	const preMainElem = useMemo(
		() => (hideGSN ? null : isCustomHomeVisible && <CustomHomeItem />),
		[hideGSN, isCustomHomeVisible],
	);

	// GlobalSideNavigation is rendered with the same props whether or not the invited-app-shortcuts
	// slot is wired; the invited-app decoration is applied entirely inside the slot, so the top-level
	// nav structure is identical on the server and on the client's first render.
	const sharedGlobalSideNavigationProps = {
		app: 'confluence' as const,
		customizationStore: 'network' as const,
		testId: 'global-side-navigation',
		appLabel: 'Confluence',
		cloudId,
		isAnonymous,
		preMainElem,
		// EMPTY_CONFIG (a module-level constant) rather than an inline `{}` so the prop identity is
		// stable in the hideGSN case too — see the memoisation comments above.
		forYou: hideGSN ? EMPTY_CONFIG : forYouConfig,
		items: hideGSN ? EMPTY_CONFIG : itemsConfig,
		disableMenuCustomization: hideGSN,
		postItemsElem: content,
		networkStorageParams: networkStorageParamsWithQueryRef,
		shortcuts: hideGSN ? undefined : globalAppShortcuts,
		bottomElem,
		onLeftNavExpand: onExpand,
		onLeftNavCollapse: onCollapse,
		onPeekStart,
		onPeekEnd,
		onResizeEnd: mirrorSideNavWidthToLocalStorage,
		sideNavContentRef,
		defaultMenuVisibility,
	};

	// Eligibility is read INSIDE the slot by `SafeInvitedAppShortcutsRecommendations`
	// (`useLazyLoadQuery` against `platformEnv`); cloudId and userId travel through
	// `InvitedAppShortcutsSlotContext` (provided by `PlatformNavigationComponent`). Everyone else
	// renders the plain shortcuts menu.
	if (showInvitedAppShortcuts) {
		return (
			<GlobalSideNavigation
				{...sharedGlobalSideNavigationProps}
				ShortcutsSlot={InvitedAppShortcutsSlot}
			/>
		);
	}

	return <GlobalSideNavigation {...sharedGlobalSideNavigationProps} />;
}
